import { MOBILE_SCREEN_SIZE } from '@Shared/constants';
import { useThrottleFn } from '@vueuse/core';
import { getCurrentInstance, onMounted, onUnmounted, Ref, ref } from 'vue';
import { ScreenObject } from './types';

export const useScreen = (callback?: () => void): Ref<Readonly<ScreenObject>> => {
  const orientationQuery = window.matchMedia ? window.matchMedia('(orientation: portrait)') : null;
  const screen = ref<ScreenObject>({
    width: window.innerWidth,
    height: window.innerHeight,
    resolution: `${window.innerWidth}x${window.innerHeight}`,
    portrait: orientationQuery?.matches ? true : false,
    landscape: orientationQuery?.matches ? false : true,
    orientation: orientationQuery?.matches ? 'portrait' : 'landscape',
    // does not react to changes in window
    touch: 'ontouchstart' in window,
    isMobile: window.innerWidth < MOBILE_SCREEN_SIZE,
  });
  if (callback) callback();

  const updateWindowSize = () => {
    screen.value.width = window.innerWidth;
    screen.value.height = window.innerHeight;
    screen.value.resolution = `${window.innerWidth}x${window.innerHeight}`;
    screen.value.isMobile = window.innerWidth < MOBILE_SCREEN_SIZE;
    if (callback) callback();
  };

  const updateOrientation = (e: MediaQueryListEvent) => {
    screen.value.portrait = e.matches;
    screen.value.landscape = !e.matches;
    screen.value.orientation = e.matches ? 'portrait' : 'landscape';

    if (callback) callback();
  };

  // do not leak event listeners
  if (getCurrentInstance()) {
    // we used to do a debounce on updating window size, removing this because it's not a common event
    // and just looks a lot worse.  We also don't have that many systems doing regular updates on sizes.
    const resizeEvent = useThrottleFn(updateWindowSize, 100);

    onMounted(() => {
      window.addEventListener('resize', resizeEvent);

      if (orientationQuery?.addEventListener) {
        orientationQuery?.addEventListener('change', updateOrientation);
      } else {
        // quite a few iOS devices use a slightly older safari which doesn't support addEventListener yet
        orientationQuery?.addListener(updateOrientation);
      }
    });

    onUnmounted(() => {
      window.removeEventListener('resize', resizeEvent);

      if (orientationQuery?.removeEventListener) {
        orientationQuery?.removeEventListener('change', updateOrientation);
      } else {
        // quite a few iOS devices use a slightly older safari which doesn't support removeEventListener yet
        orientationQuery?.removeListener(updateOrientation);
      }
    });
  }

  return screen;
};
